'use client';

import { I18nProvider } from 'next-localization';
import { ErrorBoundary } from 'react-error-boundary';
import { JSX, useEffect, useRef } from 'react';
import scConfig from 'sitecore.config';
import Bootstrap from 'src/Bootstrap';
import { SitecorePageProps } from 'lib/page-props';
import ClientHead from 'lib/client-head/ClientHead';
import { RemixIconCriticalSprite } from 'helpers/RemixIcon/RemixIconCriticalSprite';
import { RemixSpriteLoader } from 'helpers/RemixIcon/RemixIconSpriteLoader';
import { APIProvider } from '@vis.gl/react-google-maps';
import { usePathname } from 'next/navigation';
import { handleEditorFastRefresh } from '@sitecore-content-sdk/nextjs/utils';
import { FallbackProps } from 'react-error-boundary';
import { SWRConfig, type Cache } from 'swr';

/** Persists SWR data across App Router navigations (SitecoreRoot remounts each route). */
const swrCache = new Map<string, unknown>();

function getSwrCache(): Cache {
  return swrCache as Cache;
}

type SitecoreRootProps = {
  pageProps: SitecorePageProps;
  children: React.ReactNode;
};

function AppRouterErrorFallback({ resetErrorBoundary }: FallbackProps): JSX.Element {
  const pathname = usePathname();
  const prevPath = useRef(pathname);
  useEffect(() => {
    if (prevPath.current !== pathname) {
      prevPath.current = pathname;
      resetErrorBoundary();
    }
  }, [pathname, resetErrorBoundary]);

  return (
    <div style={{ padding: 24 }}>
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred while rendering this page.</p>
      <button type="button" onClick={resetErrorBoundary}>
        Try again
      </button>
      <p>
        <a href="/">Go to the home page</a>
      </p>
    </div>
  );
}

/**
 * Client shell: Bootstrap, i18n (`next-localization`), error boundary, maps, editor fast refresh.
 */
export default function SitecoreRoot({ pageProps, children }: SitecoreRootProps): JSX.Element {
  const { dictionary } = pageProps;
  const locale = pageProps.page?.locale || scConfig.defaultLanguage;
  const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    handleEditorFastRefresh();
  }, []);

  return (
    <>
      <ClientHead>
        {pageProps.contentStyles && (
          <style dangerouslySetInnerHTML={{ __html: pageProps.contentStyles }} />
        )}
      </ClientHead>
      <Bootstrap {...pageProps} />
      <RemixIconCriticalSprite />
      <RemixSpriteLoader />
      <I18nProvider lngDict={dictionary} locale={locale}>
        <ErrorBoundary fallbackRender={(props) => <AppRouterErrorFallback {...props} />}>
          <SWRConfig
            value={{
              provider: getSwrCache,
              revalidateOnFocus: false,
              revalidateOnReconnect: false,
              revalidateIfStale: false,
            }}
          >
            <APIProvider apiKey={googleMapsApiKey}>{children}</APIProvider>
          </SWRConfig>
        </ErrorBoundary>
      </I18nProvider>
    </>
  );
}
